import { IVeriTableHeader, IVeriTableRow } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { SortType } from "@enums/SortType";
import { IGroupFilter } from "@interfaces/filters/IGroupFilter";
import { IDriver } from "@interfaces/IDriver";
import { IGeofence } from "@interfaces/IGeofences";
import { IVehicle } from "@interfaces/IVehicle";
import { DriverService } from "@services/driver.service";
import { GeofenceService } from "@services/geofence.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import { TmsModuleNavigation } from "@tms/tms.navigation";
import { RouteStatus } from "@tms/_enums/RouteStatus";
import { IPlannedRoute } from "@tms/_models/IPlannedRoute";
import { IPlannedRouteDetail, PlannedRouteService } from "@tms/_services/planned-route.service";
import * as angular from "angular";
import "./arrivalsBoard.component.scss";

class ComponentController {
    updateInterval: angular.IPromise<any>;

    routes: IPlannedRouteDetail[];
    displayRoutes: IPlannedRouteDetail[];
    drivers: IDriver[];
    vehicles: IVehicle[];

    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IPlannedRoute>[] = [];
    selectedRoute: IPlannedRoute;

    loading: boolean;

    static $inject = ["$q", "$interval", "driverService", "geofenceService", "plannedRouteService", "translationService", "vehicleService", "veriDate", "veriTableService"]

    constructor(
        private readonly $q: angular.IQService,
        private readonly $interval: angular.IIntervalService,
        private readonly driverService: DriverService,
        private readonly geofenceService: GeofenceService,
        private readonly plannedRouteService: PlannedRouteService,
        private readonly translationService: TranslationService,
        private readonly vehicleService: VehicleService,
        private readonly veriDate: VeriDate,
        private readonly veriTableService: VeriTableService
    ) {
    }

    $onInit() {
        this.createTableHeaders();
        this.loadData(true);

        this.updateInterval = this.$interval(() => {
            this.loadData();
        }, 1000 * 60);
    }

    $onDestroy(): void {
        if (this.updateInterval) {
            this.$interval.cancel(this.updateInterval);
        }
    }

    selectedRouteChanged(route: IPlannedRouteDetail) {
        this.selectedRoute = route;
        this.plannedRouteService.openRouteDetailsDialog(route, TmsModuleNavigation.ArrivalsBoard);
        this.selectedRoute = null;
    }


    groupFilterChanged(filter: IGroupFilter) {
        this.displayRoutes = [];
        if (filter.group) {
            const group = filter.group;
            this.displayRoutes = this.routes.filter(route => {
                return group.VehicleIds.includes(route.VehicleId) || group.DriverIds.includes(route.DriverId);
            })
        } else {
            this.displayRoutes = this.routes;
        }
        this.createTableRows();
    }

    // ---

    private loadData(showLoadingSpinner = false, invalidateCache = false) {
        if (showLoadingSpinner) {
            this.loading = true;
        }
        let start = this.veriDate.createMoment(new Date()).startOf('day');
        let end = this.veriDate.createMoment(new Date()).endOf('day');

        let routesPromise = this.plannedRouteService.getPlannedRoutes(start, end, { mapDrivers: true, mapVehicles: true, mapGeofences: true, getUnplannedStops: true, invalidateCache: invalidateCache });
        let driversPromise = this.driverService.getDrivers();
        let vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        let geofencesPromise = this.geofenceService.getGeofences();

        this.$q.all([routesPromise, driversPromise, vehiclesPromise, geofencesPromise])
            .then(([routes, drivers, vehicles, geofences]) => {
                this.routes = this.displayRoutes = this.filterOutInactiveRoutes(routes);
                this.drivers = drivers;
                this.vehicles = vehicles;
                this.getRouteDetails(geofences);
                this.createTableRows();
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private filterOutInactiveRoutes(routes: IPlannedRouteDetail[]): IPlannedRouteDetail[] {
        let activeRoutes: IPlannedRouteDetail[] = [];

        routes.forEach(route => {
            if ((route.Status === RouteStatus.Planned || route.Status === RouteStatus.Started)) {
                activeRoutes.push(route);
            }
        });

        return activeRoutes;
    }

    private getRouteDetails(geofences: IGeofence[]) {
        this.routes.forEach(route => {
            this.plannedRouteService.assignDriverToRoute(route, this.drivers);
            this.plannedRouteService.assignVehicleToRoute(route, this.vehicles);
            this.plannedRouteService.setFirstAndLastVisit(route);
            this.plannedRouteService.mapGeofenceToVisit(route.firstVisit, geofences);
            this.plannedRouteService.mapGeofenceToVisit(route.lastVisit, geofences);
            this.plannedRouteService.setDisplayLabelForPlannedVisit(route.firstVisit);
            this.plannedRouteService.setDisplayLabelForPlannedVisit(route.lastVisit);
            this.plannedRouteService.setRouteDisplayStatus(route);
        });
    }

    private createTableHeaders() {
        this.tableHeaders = [
            { title: this.translationService.t('Tms.Progress'), width: 80, hideOnMobile: true },
            {
                title: `${this.translationService.t('Common.Driver')} / ${this.translationService.t('Common.Vehicle')}`,
                sortType: SortType.String,
                sortable: true,
                width: 250
            },
            { title: this.translationService.t("Common.Status"), sortType: SortType.String, sortable: true, width: 180, hideOnMobile: true },
            { title: this.translationService.t("Tms.ReturnTime"), sortType: SortType.Date, sortable: true, width: 120 },
            { title: this.translationService.t("Tms.ReturnETA"), sortType: SortType.Date, sortable: true, width: 130, hideOnMobile: true },
            { title: this.translationService.t("Tms.StartLocation"), sortType: SortType.String, sortable: true, hideOnMobile: true },
            { title: this.translationService.t("Tms.EndLocation"), sortType: SortType.String, sortable: true, hideOnMobile: true }
        ];
    }

    private createTableRows() {
        this.tableRows = [];
        this.displayRoutes.forEach(route => {

            this.tableRows.push({
                data: route,
                cells: this.createTableRowCells(route),
                searchString: ''
            });
        });
    }

    private createTableRowCells(route: IPlannedRouteDetail) {
        return [
            {
                contents: `<active-route-progress [route]="row.data"></active-route-progress>`
            },
            this.veriTableService.createDriverSummaryPanelCell(route.driver, route.vehicle, this.veriDate.createMoment(route.firstVisit.PlannedArrivalTime)),
            {
                contents: route.routeDisplayStatus,
                sortData: route.routeDisplayStatus
            },
            this.veriTableService.createDateTimeCell(route.lastVisit.PlannedArrivalTime, "time"),
            {
                contents: this.veriDate.formatDate(this.veriDate.createMoment(route.lastVisit.PlannedArrivalTime), 'timeago'),
                sortData: route.lastVisit.PlannedArrivalTime
            },
            {
                contents: route.firstVisit.displayNameAddressLabel,
                sortData: route.firstVisit.displayNameAddressLabel,
                showTooltip: true
            },
            {
                contents: route.lastVisit.displayNameAddressLabel,
                sortData: route.lastVisit.displayNameAddressLabel,
                showTooltip: true
            }
        ];
    }
}

export class ArrivalsBoardComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = ComponentController;
        this.template = require("./arrivalsBoard.component.html");
    }
}
